import React from 'react';
import { Outlet, useNavigate } from '@tanstack/react-router';
import { Stack, Play, TestTube } from '@phosphor-icons/react';
import { useSubNav } from '../../../hooks/useSubNav';
import { useAuth } from '../../../contexts/AuthContext';
import type { SubNavConfig } from '../../../contexts/SubNavContext';

/**
 * "Suites" section: persisted, layered test suites plus the run history. This
 * is the renamed home of what used to be the "Runs" section's test runs —
 * QA moved to the Labs tabs under /runs, and Exploratory tests graduated to
 * their own page.
 */
export function SuitesLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Gate the whole section behind the per-org flag. Once /me has loaded, send
  // disabled orgs home (the API also rejects suite calls as defense-in-depth).
  const suitesEnabled = user?.features?.suiteBuilderV2;
  const externalTestsEnabled = Boolean(user?.features?.externalTestSuites);
  React.useEffect(() => {
    if (user && !suitesEnabled) navigate({ to: '/' });
  }, [user, suitesEnabled, navigate]);

  const navConfig = React.useMemo<SubNavConfig>(
    () => ({
      title: 'Suites',
      basePath: '/suites',
      items: [
        { id: 'runs', label: 'Test Runs', icon: <Play size={16} />, to: '/runs/test' },
        ...(externalTestsEnabled
          ? [
              {
                id: 'external-tests',
                label: 'External Tests',
                icon: <TestTube size={16} />,
                to: '/runs/external-tests',
              },
            ]
          : []),
        { id: 'suites', label: 'Configuration', icon: <Stack size={16} />, to: '/suites' },
      ],
      getActiveId: ({ pathname }) => {
        if (pathname.startsWith('/runs/external-tests')) return 'external-tests';
        if (pathname.startsWith('/runs/test')) return 'runs';
        if (pathname.startsWith('/suites')) return 'suites';
        return undefined;
      },
    }),
    [externalTestsEnabled]
  );

  useSubNav(navConfig);

  if (user && !suitesEnabled) return null;
  return <Outlet />;
}
